import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { LibraryCollectionCard } from "@digital-twins/shared-types";
import { CollectionSharingBadge } from "./CollectionSharingBadge";
import { LibraryNavigationRail } from "./LibraryNavigationRail";
import type {
  CollectionsRailGroupKey,
  CollectionsRailGroup,
  CollectionsRailProps,
} from "@/types/components/library-navigation";
export type {
  CollectionsRailGroupKey,
  CollectionsRailGroup,
  CollectionsRailProps,
} from "@/types/components/library-navigation";

const GROUP_ORDER: Array<CollectionsRailGroupKey> = [
  "mine",
  "shared",
  "savedFilters",
];

const GROUP_LABEL_KEYS = {
  mine: "library.rail.groups.mine",
  shared: "library.rail.groups.shared",
  savedFilters: "library.rail.groups.savedFilters",
} as const satisfies Record<CollectionsRailGroupKey, string>;

/**
 * Mine (the viewer's curated collections), Shared with me (someone else's
 * curated collections the viewer can see) and Saved filters (every visible
 * saved filter, the viewer's first). Empty groups are dropped; each group
 * keeps the list's order (`updated_at desc`).
 */
export function groupCollectionsForRail(
  collections: Array<LibraryCollectionCard>,
): Array<CollectionsRailGroup> {
  const byGroup: Record<
    CollectionsRailGroupKey,
    Array<LibraryCollectionCard>
  > = {
    mine: [],
    shared: [],
    savedFilters: [],
  };
  for (const collection of collections) {
    if (collection.origin === "saved_filter") {
      byGroup.savedFilters.push(collection);
    } else if (collection.isOwner) {
      byGroup.mine.push(collection);
    } else {
      byGroup.shared.push(collection);
    }
  }
  byGroup.savedFilters.sort((a, b) => Number(b.isOwner) - Number(a.isOwner));
  return GROUP_ORDER.filter((key) => byGroup[key].length > 0).map((key) => ({
    key,
    collections: byGroup[key],
  }));
}

export function collectionsRailStorageKey(
  storageScope: string | null | undefined,
) {
  return `library-collections-rail-collapsed:${storageScope || "anonymous"}`;
}

export function CollectionsRail({
  collections,
  isLoading,
  hasError,
  onSelect,
  onSelectAll,
  onNewCollection,
  activeToken = null,
  isLibraryNarrowed = false,
  storageScope,
}: CollectionsRailProps) {
  const { t } = useTranslation("app");
  const groups = useMemo(
    () => groupCollectionsForRail(collections),
    [collections],
  );
  return (
    <LibraryNavigationRail
      title={t("library.rail.title")}
      allLabel={t("library.rail.all")}
      newLabel={t("library.rail.newCollection")}
      collapseLabel={t("library.rail.collapse")}
      expandLabel={t("library.rail.expand")}
      storageKey={collectionsRailStorageKey(storageScope)}
      isLoading={isLoading}
      hasError={hasError}
      errorLabel={t("library.rail.loadFailed")}
      onSelect={onSelect}
      onSelectAll={onSelectAll}
      onNew={onNewCollection}
      activeToken={activeToken}
      isAllActive={activeToken == null && !isLibraryNarrowed}
      testIdPrefix="library-rail"
      groups={groups.map((group) => ({
        key: group.key,
        label: t(GROUP_LABEL_KEYS[group.key]),
        rows: group.collections.map((collection, index) => {
          const image = collection.cover.find(
            (cover) => cover.imageUrl,
          )?.imageUrl;
          return {
            token: collection.token,
            name: collection.name,
            count:
              Object.values(collection.resourceCounts).reduce(
                (sum, count) => sum + count,
                0,
              ) + collection.topicCount,
            thumbnail: image ? (
              <img
                src={image}
                alt=""
                decoding="async"
                loading="lazy"
                className="h-full w-full rounded-md object-cover"
              />
            ) : undefined,
            decoration: (
              <CollectionSharingBadge
                sharingMode={collection.sharingMode}
                testId={`library-rail-${group.key}-${index}-sharing`}
              />
            ),
            testId: `library-rail-${group.key}-${index}`,
          };
        }),
      }))}
    />
  );
}
